/**
 * Application configuration management
 * 
 * This file provides // Auth configuration
function getAuthConfig(supabaseConfig: SupabaseConfig): AuthConfig {
  const baseUrl = getBaseUrl();
  
  const config: AuthConfig = {
    redirectUrls: {
      base: baseUrl,
      callback: `${baseUrl}/auth/callback`
    }
  };
  
  // Add email testing config only for local Supabase development
  if (supabaseConfig.isDevelopment) {
    config.emailTesting = {
      inbucketUrl: import.meta.env.VITE_EMAIL_TESTING_INBUCKET_URL || 'http://127.0.0.1:54324'
    };
  }
  
  return config;
}nfiguration without polluting
 * business logic with environment checks. All environment detection
 * happens here, and the rest of the app uses clean interfaces.
 */

// Types for type safety
interface SupabaseConfig {
  url: string;
  anonKey: string;
  isDevelopment: boolean;
}

interface AuthConfig {
  redirectUrls: {
    base: string;
    callback: string;
  };
  emailTesting?: {
    inbucketUrl: string;
  };
}

interface AppConfig {
  supabase: SupabaseConfig;
  auth: AuthConfig;
  environment: 'development' | 'production';
}

// Environment detection (happens once, at startup)
const isDevelopment = import.meta.env.DEV;

// Get current base URL dynamically
function getBaseUrl(): string {
  if (typeof window === 'undefined') {
    return 'http://localhost:3000'; // SSR fallback
  }
  
  const { protocol, hostname, port } = window.location;
  
  // Development detection
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:${port}`;
  }
  
  // Production
  return `${protocol}//${hostname}`;
}

// Supabase configuration
function getSupabaseConfig(): SupabaseConfig {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  if (!url || !anonKey) {
    throw new Error(
      'Missing Supabase configuration. Please check your environment variables.'
    );
  }
  
  return {
    url,
    anonKey,
    // Simple detection: if URL contains localhost/127.0.0.1, it's local development
    isDevelopment: url.includes('127.0.0.1') || url.includes('localhost')
  };
}

// Auth configuration
function getAuthConfig(supabaseConfig: SupabaseConfig): AuthConfig {
  const baseUrl = getBaseUrl();
  
  const config: AuthConfig = {
    redirectUrls: {
      base: baseUrl,
      callback: `${baseUrl}/auth/callback`
    }
  };
  
  // Add development-specific config only for local Supabase
  if (supabaseConfig.isDevelopment) {
    config.emailTesting = {
      inbucketUrl: 'http://localhost:54324'
    };
  }
  
  return config;
}

// Main configuration object (immutable after creation)
const supabaseConfig = getSupabaseConfig();
export const config: AppConfig = {
  supabase: supabaseConfig,
  auth: getAuthConfig(supabaseConfig),
  environment: isDevelopment ? 'development' : 'production'
};

// Utility functions for common checks
export const configUtils = {
  isDevelopment: () => config.environment === 'development',
  isProduction: () => config.environment === 'production',
  isLocalSupabase: () => config.supabase.isDevelopment,
  getEmailTestingUrl: () => config.auth.emailTesting?.inbucketUrl,
  
  // Simple debugging helper
  logConfig: () => {
    if (config.environment === 'development') {
      // eslint-disable-next-line no-console
      console.log('🔧 App Config:', {
        environment: config.environment,
        supabaseUrl: config.supabase.url,
        isLocalSupabase: config.supabase.isDevelopment,
        baseUrl: config.auth.redirectUrls.base,
        hasEmailTesting: !!config.auth.emailTesting
      });
    }
  },
  
  // Validation
  validateConfig: () => {
    const required = [
      config.supabase.url,
      config.supabase.anonKey,
      config.auth.redirectUrls.base
    ];
    
    const missing = required.filter(val => !val);
    if (missing.length > 0) {
      throw new Error(`Missing required configuration: ${missing.join(', ')}`);
    }
  }
};

// Validate on import
configUtils.validateConfig();
